import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Gamepad2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { authenticated, user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    if (authenticated && user) {
      if (user.siteId) {
        navigate('/app/dashboard');
      } else {
        navigate('/onboarding');
      }
    }
  }, [authenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        formData,
        { withCredentials: true }
      );
      
      // Store session token in localStorage as backup
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      toast.success('Connexion réussie !');
      await checkAuth(); // Refresh user data
      
      if (response.data.user.siteId) {
        navigate('/app/dashboard');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Erreur login:', error);
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B132B] p-4">
      <div className="w-full max-w-md">
        <div className="glass-effect rounded-2xl p-8 shadow-2xl">
          {/* Logo Manette Doré */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-[#C6A15B] to-[#B89149] rounded-2xl flex items-center justify-center shadow-xl">
              <Gamepad2 className="w-14 h-14 text-[#0B132B]" strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#EAEAEA] mb-1 tracking-wide">PlayCity</h1>
            <h2 className="text-2xl font-semibold text-[#EAEAEA] mb-2 tracking-[0.25em]">Immersion</h2>
            <p className="text-xs text-[#EAEAEA]/60 mb-2">Jouez, Apprenez, Vivez l'immersion !</p>
            <p className="text-[#C6A15B] text-lg">Caisse & Gestion</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#EAEAEA] mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] focus:border-[#C6A15B]"
                placeholder="admin@playcity.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#EAEAEA] mb-2 block">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] focus:border-[#C6A15B] pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C6A15B] hover:text-[#B89149] transition-colors"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              data-testid="login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B] font-semibold py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>

            <div className="text-center text-sm text-[#EAEAEA]/60 mt-4">
              <p>Salle de jeux & loisirs • Abidjan</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
